import { type CostCenter } from '@einwert/valuation-api-client'
import {
    ContactListItem,
    CostCenterSelect,
    CustomerUserAssignment,
    FormDatePicker,
    FormTemplate,
    FormTextField,
    HeadlineWithSpacing,
    joinDefinedStrings,
    LoadingSkeleton,
    MandatorListItem,
    PageGridItem,
    toIsoDateString,
    tokens,
    Typography,
    UsageTypeMultiSelect,
    ValuationReasonAutocomplete,
} from 'ComponentLibrary'
import { AssetsData, OnSubmit, type StepProps } from '../types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { GridLegacy as Grid, Stack, styled, useTheme } from '@mui/material'
import { isEmpty } from 'lodash'
import invariant from 'tiny-invariant'
import dayjs from 'dayjs'
import { OrderValuationStepFormValues } from './types'
import { FunnelFooter } from 'ClientPortal/src/pages/Orders/CreateOrder/FunnelFooter/FunnelFooter'
import type { CustomerUser } from '@einwert/valuation-api-client'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReasonForValuation } from 'ComponentLibrary/src/common/valuation-reason'
import { USAGE_TYPE_OPTIONS, UsageTypeOption } from 'ComponentLibrary/src/molecules/UsageTypeMultiSelect/UsageTypeMultiSelect'
import { ValuationReasonAutocompleteOption } from 'ComponentLibrary/src/molecules/ValuationReasonAutocomplete'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

const VALUATION_REASON_GROUPS: { groupKey: string; options: ReasonForValuation[] }[] = [
    { groupKey: 'recurringValuation', options: ['recurring_valuation'] },
    { groupKey: 'legalReasons', options: ['division_of_wealth', 'court_proceedings'] },
    { groupKey: 'accounting', options: ['accounting_purposes', 'tax_purposes', 'intrinsic_value_check'] },
    { groupKey: 'transactions', options: ['purchase', 'sale', 'partial_sale', 'auction', 'financing_purposes'] },
]

export function OrderValuationStep({
    order,
    costCenterOptions,
    isCostCentersPending,
    activeStep,
    handleBack,
    customerUsers,
    isLastStep = false,
    isCreatingOrder = false,
    isCustomerUsersPending,
    onSubmit,
    dispatch,
    assetsData,
    createNewCostCenter,
    isCreatingNewCostCenter,
}: Omit<StepProps, 'handleNext'> & {
    onSubmit: OnSubmit
    isCreatingOrder?: boolean
    customerUsers: CustomerUser[]
    assetsData: AssetsData
    costCenterOptions: CostCenter[]
    isCostCentersPending?: boolean
    isCustomerUsersPending?: boolean
    createNewCostCenter: (costCenter: Omit<CostCenter, 'uuid'>) => void
    isCreatingNewCostCenter?: boolean
}) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const theme = useTheme()
    const [isEditCustomerUserAssignmentFormOpen, setIsEditCustomerUserAssignmentFormOpen] = useState(false)
    const valuationReasonOptions = useMemo(
        () =>
            VALUATION_REASON_GROUPS.flatMap(({ groupKey, options }) => {
                const groupLabel = tTerminology(`valuationReasonGroups.${groupKey}`)
                return options
                    .map((value) => ({ value, label: tTerminology(`valuationReasons.${value}`), groupLabel }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            }) satisfies ValuationReasonAutocompleteOption[],
        [tTerminology]
    )
    const usageTypeOptions = useMemo(
        () =>
            USAGE_TYPE_OPTIONS.map((option) => {
                const labelKey = option.value.sub_usage_type ?? option.value.usage_type
                return { ...option, label: tTerminology(`usageTypes.${labelKey}`) }
            }) satisfies UsageTypeOption[],
        [tTerminology]
    )
    const costCenterCopy = {
        inputLabel: t('valuation.costCenter.inputLabel'),
        addNewLabel: t('valuation.costCenter.addNewLabel'),
        formLabels: {
            name: t('valuation.costCenter.form.name'),
            description: t('valuation.costCenter.form.description'),
        },
    }
    const userLinkCopy = useUserLinkCopy()
    const customerAssignmentCopy = {
        addButton: t('valuation.customerAssignment.addButton'),
        modalTitle: t('valuation.customerAssignment.modalTitle'),
        cancel: t('valuation.customerAssignment.cancel'),
        duplicateWarning: t('valuation.customerAssignment.duplicateWarning'),
        assigneeLabel: t('valuation.customerAssignment.assigneeLabel'),
        removeTooltip: t('valuation.customerAssignment.removeTooltip'),
        addMore: t('valuation.customerAssignment.addMore'),
    }
    const contactFormCopy = {
        salutation: t('valuation.contactForm.salutation'),
        salutationOptions: {
            mr: t('valuation.contactForm.salutationOptions.mr'),
            ms: t('valuation.contactForm.salutationOptions.ms'),
            dr: t('valuation.contactForm.salutationOptions.dr'),
            mrDr: t('valuation.contactForm.salutationOptions.mrDr'),
            msDr: t('valuation.contactForm.salutationOptions.msDr'),
            placeholder: t('valuation.contactForm.salutationOptions.placeholder'),
        },
        name: t('valuation.contactForm.name'),
        role: t('valuation.contactForm.role'),
        company: t('valuation.contactForm.company'),
        street: t('valuation.contactForm.street'),
        houseNumber: t('valuation.contactForm.houseNumber'),
        zip: t('valuation.contactForm.zip'),
        city: t('valuation.contactForm.city'),
        email: t('valuation.contactForm.email'),
        invalidEmail: t('valuation.contactForm.invalidEmail'),
        phoneNumber: t('valuation.contactForm.phoneNumber'),
        invalidPhoneNumber: t('valuation.contactForm.invalidPhoneNumber'),
        note: t('valuation.contactForm.note'),
        infoBox: t('valuation.contactForm.infoBox'),
    }
    const contactListItemCopy = {
        edit: t('valuation.contactListItem.edit'),
        delete: t('valuation.contactListItem.delete'),
        editModalTitle: t('valuation.contactListItem.editModalTitle'),
        form: contactFormCopy,
    }

    const defaultValues = {
        valuationDate: order.valuation_date ? dayjs(order.valuation_date) : undefined,
        valuationReason: order.valuation_reason,
        additionalInformation: order.note,
        assetUsageTypes: order.asset_usage_types,
    }
    const methods = useForm<OrderValuationStepFormValues>({
        defaultValues,
    })

    const {
        formState: { errors },
        control,
        handleSubmit,
        getValues,
    } = methods

    const handleNext = handleSubmit((data: OrderValuationStepFormValues) => {
        invariant(data.valuationReason !== '', 'Valuation reason is required')
        onSubmit({
            ...order,
            valuation_date: data.valuationDate ? toIsoDateString(data.valuationDate) : undefined,
            valuation_reason: data.valuationReason,
            note: data.additionalInformation,
        })
    })

    const onHandleBack = () => {
        const [valuationDate, valuationReason, note] = getValues(['valuationDate', 'valuationReason', 'additionalInformation'])
        dispatch({
            type: 'updateOrder',
            payload: {
                ...order,
                valuation_date: valuationDate ? toIsoDateString(valuationDate) : undefined,
                valuation_reason: valuationReason,
                asset_usage_types: order.asset_usage_types,
                note,
            },
        })
        handleBack()
    }

    //This function will not get triggered until the changes to make the backend's address field optional
    const getAddressFromAssetId = () => {
        const assets = assetsData.multiAssetValueOverviewData?.assets
        const foundAsset = assets?.find((asset) => asset.uuid === order.asset_uuid)
        return foundAsset?.address
    }
    const address = order.address ?? getAddressFromAssetId()

    const getAssetCostCenter = () => {
        const assets = assetsData.multiAssetValueOverviewData?.assets
        const foundAsset = assets?.find((asset) => asset.uuid === order.asset_uuid)
        const costCenterId = foundAsset?.cost_center_uuid
        return costCenterOptions.find((costCenter) => costCenter.uuid === costCenterId)
    }

    const getOrderCostCenter = () => {
        const costCenterId = order?.cost_center_uuid
        return costCenterOptions.find((costCenter) => costCenter.uuid === costCenterId)
    }

    return (
        <PageGridItem columnSpan={3} columnSpanXs={4}>
            <Stack direction="column" gap={theme.spacing(6)} maxWidth="520px">
                <FormTemplate>
                    <Grid item xs={6}>
                        <FormProvider {...methods}>
                            <FormDatePicker
                                label={t('valuation.valuationDate')}
                                name="valuationDate"
                                control={control}
                                errors={errors}
                                locale={locale}
                            />
                        </FormProvider>
                    </Grid>
                    <Grid item xs={6} data-testid="Auftragszweck *-select">
                        <Controller
                            control={control}
                            name="valuationReason"
                            rules={{ required: { value: true, message: t('validation.requiredField') } }}
                            render={({ field }) => (
                                <ValuationReasonAutocomplete
                                    label={t('valuation.valuationReason')}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.valuationReason}
                                    helperText={errors.valuationReason?.message || ''}
                                    options={valuationReasonOptions}
                                />
                            )}
                        />
                    </Grid>
                    {!order.asset_uuid && (
                        <UsageTypeMultiSelect
                            defaultValues={defaultValues.assetUsageTypes}
                            label={t('valuation.assetUsageTypes')}
                            tooltipText={t('valuation.assetUsageTypesTooltip')}
                            options={usageTypeOptions}
                            unknownLabel={tTerminology('usageTypes.unknown')}
                            setValue={(newAssetUsageTypes) =>
                                dispatch({
                                    type: 'replaceAssetUsageTypes',
                                    payload: newAssetUsageTypes,
                                })
                            }
                        />
                    )}
                    <FormTextField
                        label={t('valuation.additionalInformation')}
                        control={control}
                        fieldName="additionalInformation"
                        errors={errors}
                        multiline
                        fullWidth
                        resize="vertical"
                    />
                    <Grid item xs={12}>
                        {isCostCentersPending || isCreatingNewCostCenter ? (
                            <LoadingSkeleton height="100%" variant="rounded" />
                        ) : (
                            <CostCenterSelect
                                defaultValue={getOrderCostCenter() || getAssetCostCenter() || ''}
                                options={costCenterOptions}
                                selectCostCenter={(costCenterId) => dispatch({ type: 'updateCostCenter', payload: costCenterId })}
                                createNewCostCenter={createNewCostCenter}
                                copy={costCenterCopy}
                            />
                        )}
                    </Grid>
                    <HeadlineWithSpacing title={t('valuation.assignees')} variant="headline2" />
                    <Grid item xs={6}>
                        {isCustomerUsersPending ? (
                            <LoadingSkeleton height="56px" variant="rounded" />
                        ) : (
                            <CustomerUserAssignment
                                customerUsers={customerUsers ?? []}
                                assignedCustomerUserIds={order.assigned_customer_users}
                                open={isEditCustomerUserAssignmentFormOpen}
                                setOpen={setIsEditCustomerUserAssignmentFormOpen}
                                isPersistingAssignedCustomerUsers={isCreatingOrder}
                                onSubmitCallback={(userIds) => {
                                    dispatch({ type: 'updateAssignedCustomerUsers', payload: userIds })
                                    setIsEditCustomerUserAssignmentFormOpen(false)
                                }}
                                copy={customerAssignmentCopy}
                                userLinkCopy={userLinkCopy}
                            />
                        )}
                    </Grid>
                </FormTemplate>
            </Stack>
            {!isEmpty(order.mandators) && (
                <>
                    <HeadlineWithSpacing title={tTerminology('mandators')} variant="headline2" />
                    <StyledListWrapper style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
                        {order.mandators.map((mandator, index) => (
                            <MandatorListItem key={index} mandator={mandator} />
                        ))}
                    </StyledListWrapper>
                </>
            )}
            {!isEmpty(order.contacts) && <HeadlineWithSpacing title={t('valuation.additionalContacts')} variant="headline2" />}
            <Stack direction="column" gap={tokens.SizingSpacing200}>
                {order.contacts.map((contact, index) => (
                    <ContactListItem key={index} contact={contact} copy={contactListItemCopy} userLinkCopy={userLinkCopy} />
                ))}
            </Stack>
            <HeadlineWithSpacing title={tTerminology('asset')} variant="headline2" />
            <BlackTypography variant="bodyTextLarge">{joinDefinedStrings([address?.street, address?.house_number])}</BlackTypography>
            <BlackTypography variant="bodyTextLarge">{joinDefinedStrings([address?.zip, address?.city])}</BlackTypography>
            <BlackTypography variant="bodyTextLarge" style={{ marginTop: theme.spacing(2) }}>
                {address?.state}
            </BlackTypography>
            <BlackTypography variant="bodyTextLarge">{address?.country}</BlackTypography>
            {order.customer_asset_reference && (
                <BlackTypography variant="bodyTextLarge" style={{ marginTop: theme.spacing(2) }}>
                    {joinDefinedStrings([t('valuation.objectId'), order.customer_asset_reference])}
                </BlackTypography>
            )}
            <FunnelFooter
                activeStep={activeStep}
                isLastStep={isLastStep}
                handleNext={handleNext}
                handleBack={onHandleBack}
                isPending={isCreatingOrder}
            />
        </PageGridItem>
    )
}

const BlackTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.customtext.black100,
}))

const StyledListWrapper = styled('ul')(() => ({
    listStyle: 'none',
    padding: 0,
}))
